import { reverseRecord } from '@/util';

import type { TemplatePlaceholder } from './templatePlaceholder';

// TODO: Where does this go?
export const PARTICIPATION_STATUSES = [
  'Registered',
  'Invitation Declined',
  'Attended',
  'Confirmed',
  'No-show',
  'Cancelled',
];

export const ROSTER_TYPES = [
  'Rostered',
  'Captain',
  'Set-up',
  'Pack-up',
  'Scrapper',
  'Scrutineer',
  'Self-rostered',
];

// The keys are the data being moved to and from the backend, and the
// values are the stuff being displayed. Note that this value is
// slightly different from the CONTACT_STATUS_CODE_MAPPINGS
// values on the backend

export const CONTACT_STATUS_CODE_MAPPINGS = {
  A: 'Spam',
  C: 'Complete',
  D: 'Deceased',
  N: 'Not a supporter',
  O: 'Opting out of EMS/Rocket',
  P: 'Pending',
  R: 'Duplicate contact',
  S: 'Sensitive contact',
  U: 'Uncontactable by phone or email',
  X: 'Needs contacting',
  '': 'Not set',
} as const;

// The keys for TASK_CHOICE_MAPPING are the values for the Choice field
// for Custom_Elections.

export const TASK_CHOICE_MAPPING = {
  Y: 'Active/Rostered/Confirmed',
  E: 'Interested',
  N: 'No',
  L: 'Team lead',
  T: 'Trained',
  U: 'Lapsed',
  I: 'Sign installed',
  D: 'Sign needs replacing',
  R: 'Sign returned',
  '': 'Unknown',
};

// The keys for TASK_KIND_MAPPING are the suffixes for the Task_kind field
// for Custom_Elections.

export const TASK_KIND_MAPPING = {
  SCRUT: 'Scrutineering',
  DN: 'Doorknocking',
  EP: 'Erect/Remove signs',
  PH: 'Phonearound',
  PP: 'Early voting',
  STALL: 'Stalls',
  OFC: 'Office',
  LB: 'Letterboxing',
  HTV: 'Election day',
  FP: 'Host a sign',
  NO: 'Neighbourhood organising',
  DE: 'Data entry',
  PHV: 'Phone voters',
  PHS: 'Phone supporters',
  TXT: 'Textbanking',
  CAP: 'Booth captain',
};

export const TASK_KIND_MAPPING_REVERSE = reverseRecord(TASK_KIND_MAPPING);
export const TASK_CHOICE_MAPPING_REVERSE = reverseRecord(TASK_CHOICE_MAPPING);

export const VOTING_PERIODS = ['Any', 'Early voting', 'Polling Day'];
export const SHIFT_STATUSES = ['Any', 'Confirmed', 'Draft'];
export const MAILING_STATUSES = ['delivered', 'unopened', 'opened'] as const;

export type ParticipantStatus = (typeof PARTICIPATION_STATUSES)[number];
export type RosterTypes = (typeof ROSTER_TYPES)[number];
export type VotingPeriods = (typeof VOTING_PERIODS)[number];
export type ShiftStatuses = (typeof SHIFT_STATUSES)[number];
export type SupporterStatus = keyof typeof CONTACT_STATUS_CODE_MAPPINGS;
export type TaskChoice = keyof typeof TASK_CHOICE_MAPPING;
export type TaskKind = keyof typeof TASK_KIND_MAPPING;
export type MailingStatus = (typeof MAILING_STATUSES)[number];

// Returns true if the task choice is compatible with the task kind; false otherwise.

export function isPermissibleTaskChoiceAndKind(taskChoice: TaskChoice, taskKind: TaskKind) {
  if (taskKind === 'FP') {
    // Hosting a sign
    return ['E', 'Y', 'I', 'D', 'R', 'N', ''].includes(taskChoice);
  } else if (['CAP', 'HTV', 'PP', 'SCRUT'].includes(taskKind)) {
    // Polling location related tasks
    return ['E', 'T', 'Y', 'N', ''].includes(taskChoice);
  } else {
    // All other tasks
    return ['E', 'T', 'Y', 'L', 'U', 'N', ''].includes(taskChoice);
  }
}

// Here, we are looking for bad combinations and flagging them. (If the arrays are empty, then
// there is no bad combination, and thus returns true.)

export function arePermissibleTaskChoicesAndKinds(
  taskChoices: TaskChoice[],
  taskKinds: TaskKind[]
) {
  if (taskChoices.length === 0 || taskKinds.length === 0) {
    return true;
  }
  for (const taskChoice of taskChoices) {
    for (const taskKind of taskKinds) {
      if (!isPermissibleTaskChoiceAndKind(taskChoice, taskKind)) {
        return false;
      }
    }
  }
  return true;
}

export type RosterCriteria = {
  votingPeriod?: VotingPeriods | TemplatePlaceholder;
  shiftStatus?: ShiftStatuses | TemplatePlaceholder;
};

const VALID_SUPPORTER_STATUSES = Object.keys(CONTACT_STATUS_CODE_MAPPINGS);
export function isSupporterStatus(value: string): value is SupporterStatus {
  return VALID_SUPPORTER_STATUSES.includes(value);
}

export type RangeOption = 'Equal to' | 'Older than or equal to' | 'Younger than or equal to';

export type AgeCriteria = {
  range: RangeOption | null;
  value: number | null | TemplatePlaceholder;
};
